import { DATA_ABSENT_REASON_SYSTEM, FINDING_STATE_SYSTEM } from "@repo/fhir-systems";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import { assertAnswerFits } from "./lib/questionnaire-answer-fit";
import { reconcileScoreObservations, scoreObservationId } from "./lib/score-observations";
import type { MemberId } from "./member-identity";
import { Observation } from "./observation.model";
import type { PatientId } from "./patient.model";
import type { PerformanceMeasureDefinition } from "./performance-measure-definition.model";
import type { PractitionerId } from "./practitioner.model";
import type { QuestionnaireAnswer } from "./questionnaire-instance.model";
import type { VersionedCanonical } from "./spine.model";
import type { VisitId } from "./visit.model";

export type PerformanceMeasureId = DomainId<"PerformanceMeasureId">;
export function asPerformanceMeasureId(value: string): PerformanceMeasureId {
  if (!value.trim()) throw new PreconditionFailedError("A performance measure identity cannot be empty");
  return value as PerformanceMeasureId;
}
export type PerformanceMeasureOutcome = "performed" | "did-not-perform" | "unable-to-perform";
export interface PerformanceMeasureFacts {
  id: PerformanceMeasureId;
  patient: PatientId;
  visit: VisitId;
  clinician: PractitionerId;
  recordedAt: string;
  definition: VersionedCanonical;
  status: "open" | "closed";
  outcome?: PerformanceMeasureOutcome;
  grades: readonly { task: MemberId; answer: QuestionnaireAnswer["value"] }[];
}
export class PerformanceMeasure {
  private data: PerformanceMeasureFacts;
  constructor(
    facts: Omit<PerformanceMeasureFacts, "definition" | "outcome" | "grades" | "status">,
    definition: PerformanceMeasureDefinition,
  ) {
    if (!facts.patient.trim() || !facts.visit.trim() || !facts.clinician.trim() || !facts.recordedAt.trim())
      throw new PreconditionFailedError("A performance measure requires a patient, visit, clinician and time");
    if (definition.availability !== "Active")
      throw new PreconditionFailedError("A performance measure requires an active definition");
    this.data = {
      id: asPerformanceMeasureId(facts.id),
      patient: facts.patient,
      visit: facts.visit,
      clinician: facts.clinician,
      recordedAt: facts.recordedAt,
      definition: { url: definition.url, version: String(definition.version) },
      status: "open",
      grades: [],
    };
  }
  static restore(facts: PerformanceMeasureFacts): PerformanceMeasure {
    const measure = Object.create(PerformanceMeasure.prototype) as PerformanceMeasure;
    measure.data = structuredClone(facts);
    return measure;
  }
  get id() {
    return this.data.id;
  }
  get facts() {
    return this.snapshot();
  }
  snapshot(): PerformanceMeasureFacts {
    return structuredClone(this.data);
  }
  grade(task: MemberId, answer: QuestionnaireAnswer["value"], definition: PerformanceMeasureDefinition): void {
    this.assertOpen();
    if (this.data.outcome) throw new PreconditionFailedError("Clear the outcome before grading");
    this.setGrade(task, answer, definition);
  }
  private setGrade(
    task: MemberId,
    answer: QuestionnaireAnswer["value"],
    definition: PerformanceMeasureDefinition,
  ): void {
    this.assertDefinition(definition);
    const item = definition.sections.flatMap((s) => s.items).find((i) => i.id === task);
    if (!item || item.kind !== "Question") throw new PreconditionFailedError("Grade must name an offered task");
    assertAnswerFits({ ...item, id: task }, answer);
    if (answer.kind === "Text" && !answer.value.trim())
      throw new PreconditionFailedError("A required task needs an answer");
    this.data.grades = [...this.data.grades.filter((g) => g.task !== task), { task, answer: structuredClone(answer) }];
  }

  closeOut(definition: PerformanceMeasureDefinition, outcome?: Exclude<PerformanceMeasureOutcome, "performed">): void {
    this.assertOpen();
    this.assertDefinition(definition);
    const selected = outcome ?? this.data.outcome;
    if (
      !selected &&
      definition.sections
        .flatMap((s) => s.items)
        .some((i) => i.kind === "Question" && !this.data.grades.some((g) => g.task === i.id))
    )
      throw new PreconditionFailedError("Grade every task before close-out");
    this.data.outcome = selected ?? "performed";
    this.data.status = "closed";
  }
  markDidNotPerform(): void {
    this.assertOpen();
    this.data.outcome = "did-not-perform";
  }
  markUnableToPerform(): void {
    this.assertOpen();
    this.data.outcome = "unable-to-perform";
  }
  clearOutcome(): void {
    this.assertOpen();
    delete this.data.outcome;
  }
  editGrade(task: MemberId, answer: QuestionnaireAnswer["value"], definition: PerformanceMeasureDefinition): void {
    if (this.data.status !== "closed" || this.data.outcome !== "performed")
      throw new PreconditionFailedError("Only a performed performance measure permits grade edits");
    if (!this.data.grades.some((grade) => grade.task === task))
      throw new PreconditionFailedError("Only a recorded grade can be edited after close-out");
    this.setGrade(task, answer, definition);
  }
  retract(): never {
    throw new PreconditionFailedError("Retracting a performance measure is not supported");
  }
  private assertOpen(): void {
    if (this.data.status === "closed")
      throw new PreconditionFailedError("Changing the outcome after close-out is not supported");
  }
  private assertDefinition(definition: PerformanceMeasureDefinition): void {
    if (definition.url !== this.data.definition.url || String(definition.version) !== this.data.definition.version)
      throw new PreconditionFailedError("The performance measure requires its pinned definition");
  }
  scoreObservationId(score: MemberId) {
    return scoreObservationId("PerformanceMeasureScore", this.id, score);
  }
  reconcileScores(definition: PerformanceMeasureDefinition, existing: readonly Observation[]): Observation[] {
    this.assertDefinition(definition);
    if (this.data.status !== "closed") return [];
    if (this.data.outcome !== "performed") {
      const unable = this.data.outcome === "unable-to-perform";
      return definition.scores
        .filter((score) => !existing.some((row) => row.id === this.scoreObservationId(score.id!)))
        .map((score) =>
          Observation.performanceMeasureScore({
            id: this.scoreObservationId(score.id!),
            measureId: this.id,
            patientId: this.data.patient,
            visit: this.data.visit,
            clinician: this.data.clinician,
            at: this.data.recordedAt,
            scoreId: score.id!,
            label: score.label,
            dataAbsentReason: {
              coding: [
                { system: DATA_ABSENT_REASON_SYSTEM, code: unable ? "error" : "not-performed" },
                ...(unable ? [{ system: FINDING_STATE_SYSTEM, code: "unable-to-perform" }] : []),
              ],
            },
          }),
        );
    }
    const answers: QuestionnaireAnswer[] = this.data.grades.map((g) => ({
      questionId: g.task,
      value: g.answer,
    }));
    return reconcileScoreObservations(
      definition,
      definition.computeScores(answers),
      existing,
      this.data.recordedAt,
      (result, label) =>
        Observation.performanceMeasureScore({
          id: this.scoreObservationId(result.scoreId),
          measureId: this.id,
          patientId: this.data.patient,
          visit: this.data.visit,
          clinician: this.data.clinician,
          at: this.data.recordedAt,
          scoreId: result.scoreId,
          label,
          value: result.value,
          unit: result.unit,
          band: result.band,
        }),
    );
  }
}
